/**
 * Meta (Facebook) Pixel — wrappers tipados por evento estándar.
 *
 * Originalmente este archivo fue convertido a no-ops cuando se asumió que
 * el Pixel había sido removido del sitio. La asunción era falsa: el script
 * `fbevents.js` sigue cargándose desde `index.html` con el ID del Pixel
 * activo. Como consecuencia los 4 callers (`PaymentModal`,
 * `MercadoPagoCheckoutForm`, `MaintenanceTemplate`, `BioLinkTemplate`)
 * dispararon a /dev/null durante meses.
 *
 * Ahora cada función llama a `fbq('track', ...)` cuando está disponible y
 * loguea en dev para diagnóstico. Errores se tragan — analytics nunca
 * rompen UI.
 *
 * Para casos que necesitan dedup browser+CAPI usar `src/lib/metaCapi.ts`
 * (helpers `trackPurchaseDual`, `trackInitiateCheckoutDual`, etc.).
 */

declare global {
  interface Window {
    fbq?: (
      command: string,
      eventName: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string },
    ) => void;
  }
}

function fireFbqStandard(
  eventName:
    | "ViewContent"
    | "Lead"
    | "Contact"
    | "InitiateCheckout"
    | "Purchase",
  params?: Record<string, unknown>,
): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[meta-pixel] ${eventName}`, params);
  }
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", eventName, params ?? {});
    }
  } catch {
    // never break the page
  }
}

function fireFbqCustom(eventName: string, params?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[meta-pixel] (custom) ${eventName}`, params);
  }
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", eventName, params ?? {});
    }
  } catch {
    // never break the page
  }
}

export function trackViewContentMeta(params?: Record<string, unknown>): void {
  fireFbqStandard("ViewContent", params);
}

export function trackLeadMeta(params?: Record<string, unknown>): void {
  fireFbqStandard("Lead", params);
}

export function trackContactMeta(params?: Record<string, unknown>): void {
  fireFbqStandard("Contact", params);
}

export function trackBestFriendInstagramMeta(
  params?: Record<string, unknown>,
): void {
  fireFbqCustom("BestFriendInstagram", params);
}

export function trackInitiateCheckoutMeta(
  params?: Record<string, unknown>,
): void {
  fireFbqStandard("InitiateCheckout", params);
}

export function trackPurchaseMeta(params: {
  value: number;
  currency: string;
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  num_items?: number;
}): void {
  fireFbqStandard("Purchase", params as Record<string, unknown>);
}
